import type {
  PropertyFilterOption,
  PropertyFilterProperty,
  PropertyFilterQuery,
  PropertyFilterToken,
} from '@cloudscape-design/collection-hooks';
import { DndContext } from '@dnd-kit/core';
import Button from '@risk-smart/themed-cloudscape-components/button';
import { Card, cn, Icon } from '@risksmart-app/atomic-ui';
import type { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import { ModalForm } from 'src/components/form/form/ModalForm';
import type { SaveAction } from 'src/components/form/form/types';
import { FilterPropertyDashboardItem } from 'src/components/register-dashboard/FilterPropertyDashboardItem';
import { sanitiseTokens } from 'src/pages/dashboards/universal-widget/sanitiseSettings';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import CustomisableRibbonForm from '@/components/customisable-ribbon/CustomisableRibbonForm';
import {
  useCanManageCustomRibbon,
  useGetRibbonItemsByParentType,
} from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { useUpsertCustomRibbon } from '../../hooks/mutations/custom-ribbon/useUpsertCustomRibbon';
import type {
  CustomisableRibbonModalFields,
  FilterModal,
} from './customisableRibbonModalSchema';
import { CustomisableRibbonFormSchema } from './customisableRibbonModalSchema';

export interface Props<T> {
  items: readonly T[] | undefined;
  /**
   * True while the register data feeding `items` — including any secondary
   * enrichment (risk scores, appetites) — is still loading. Ribbon counts are
   * computed client-side from `items`, so rendering before everything has
   * settled paints false zeros (RSP-6539). `items === undefined` is treated
   * as loading regardless; an empty array is a genuine zero.
   */
  loading?: boolean;
  propertyFilterQuery: PropertyFilterQuery;
  onFilterQueryChanged: (query: PropertyFilterQuery) => void;
  filteringProperties: readonly PropertyFilterProperty[];
  filteringOptions: readonly PropertyFilterOption[] | undefined;
  parentType?: Parent_Type_Enum;
  defaultFilters: FilterModal[];
  onFiltersChange?: (filters: FilterModal[]) => void;
  clickThroughEnabled?: boolean;
  noClickthroughMessageContent?: string;
  /**
   * Extra classes applied to the inner flex row. Used by the My Items page to
   * match the padding-bottom baked into RegisterDashboard so the two ribbon
   * containers line up when the right side doesn't overflow (RSP-4427).
   */
  innerClassName?: string;
  /**
   * When false, the ellipsis button that opens the edit-ribbon modal is
   * hidden regardless of the user's permissions. Defaults to true. Used to
   * suppress the custom-filter UI on screens where the feature is not yet
   * released (e.g. My Items).
   */
  showEditButton?: boolean;
  /**
   * When true, the in-ribbon edit button is not rendered; instead the ribbon
   * publishes its "Edit Filters" opener to the object-toolbar bridge store so the
   * page-header toolbar surfaces it in its overflow "Edit" group (CUS-150). The
   * edit modal stays owned by this component. Gate this at the call site on the
   * `object_toolbar` flag. Defaults to false (button renders in the ribbon).
   */
  relocateEditToToolbar?: boolean;
}

// For more information see the docs at: docs/customisable-ribbon.md
const CustomisableRibbon = <T extends object>({
  items,
  loading: itemsLoading,
  propertyFilterQuery,
  onFilterQueryChanged,
  filteringProperties,
  filteringOptions,
  parentType,
  defaultFilters,
  onFiltersChange,
  clickThroughEnabled = true,
  noClickthroughMessageContent,
  innerClassName,
  showEditButton = true,
  relocateEditToToolbar = false,
}: Props<T>) => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'customisableRibbons',
  });
  const [showEditRibbonModal, setShowEditRibbonModal] = useState(false);
  // Only relocate when a toolbar is actually mounted on this page — matches the
  // shared guard in FormMenu/PageWrapper. The `object_toolbar` flag can be on
  // globally while a page renders no toolbar; relocating there would hide the
  // in-ribbon button and publish an opener nobody consumes.
  const toolbarMounted = useObjectToolbarFormStore((s) => s.toolbarMounted);
  const setRibbonConfigState = useObjectToolbarFormStore(
    (s) => s.setRibbonConfigState
  );
  const clearRibbonConfigState = useObjectToolbarFormStore(
    (s) => s.clearRibbonConfigState
  );

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const velocityRef = useRef(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const canScrollLeftRef = useRef(false);
  const canScrollRightRef = useRef(false);
  const [scrollContainerKey, setScrollContainerKey] = useState(0);

  const assignScrollRef = useCallback((el: HTMLDivElement | null) => {
    scrollContainerRef.current = el;
    setScrollContainerKey((k) => k + 1);
  }, []);
  const [hoveredScrollBtn, setHoveredScrollBtn] = useState<
    'left' | 'right' | null
  >(null);

  const updateScrollState = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) {
      return;
    }
    const newCanScrollLeft = el.scrollLeft > 0;
    const newCanScrollRight =
      el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    canScrollLeftRef.current = newCanScrollLeft;
    canScrollRightRef.current = newCanScrollRight;
    setCanScrollLeft(newCanScrollLeft);
    setCanScrollRight(newCanScrollRight);
  }, []);

  const cancelScrollAnimation = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    velocityRef.current = 0;
  }, []);

  const stopScrolling = useCallback(() => {
    const initialVelocity = velocityRef.current;
    cancelScrollAnimation();
    if (Math.abs(initialVelocity) < 0.5) {
      return;
    }
    let velocity = initialVelocity;
    const intervalId = setInterval(() => {
      velocity *= 0.85;
      if (Math.abs(velocity) < 0.5) {
        clearInterval(intervalId);
        if (scrollIntervalRef.current === intervalId) {
          scrollIntervalRef.current = null;
        }

        return;
      }
      scrollContainerRef.current?.scrollBy({ left: velocity });
    }, 16);
    scrollIntervalRef.current = intervalId;
  }, [cancelScrollAnimation]);

  const startScrolling = useCallback(
    (direction: 'left' | 'right') => {
      cancelScrollAnimation();
      velocityRef.current = direction === 'left' ? -18 : 18;
      scrollIntervalRef.current = setInterval(() => {
        scrollContainerRef.current?.scrollBy({ left: velocityRef.current });
      }, 16);
    },
    [cancelScrollAnimation]
  );

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) {
      return;
    }
    updateScrollState();
    let rafId: ReturnType<typeof requestAnimationFrame> | null = null;
    const observer = new ResizeObserver(() => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(updateScrollState);
    });
    observer.observe(el);

    return () => {
      observer.disconnect();
      cancelScrollAnimation();
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [scrollContainerKey, updateScrollState, cancelScrollAnimation]);

  // When a chevron button unmounts mid-hold (scroll reached the boundary), its
  // onMouseUp never fires. A document-level mouseup cleans up the interval in
  // that case. We only cancel when velocity is in the direction that is now
  // blocked — this avoids interfering with normal button release, where the
  // button's own onMouseUp fires first and starts the deceleration phase.
  useEffect(() => {
    const handleMouseUp = () => {
      const vel = velocityRef.current;
      if (
        (vel > 0 && !canScrollRightRef.current) ||
        (vel < 0 && !canScrollLeftRef.current)
      ) {
        cancelScrollAnimation();
      }
    };
    document.addEventListener('mouseup', handleMouseUp);

    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [cancelScrollAnimation]);

  const { upsertCustomRibbon } = useUpsertCustomRibbon();

  // Use a stable dummy value so hook call count never changes; shouldSkip
  // prevents the query from firing when no parentType is provided.
  const parentTypeOrDummy = parentType ?? ('' as Parent_Type_Enum);
  const {
    data: responseData,
    loading,
    error,
  } = useGetRibbonItemsByParentType({
    queryArgs: { parentType: parentTypeOrDummy },
    shouldSkip: !parentType,
  });

  if (error) {
    throw error;
  }

  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const { canManage, loading: rbacLoading } =
    useCanManageCustomRibbon(parentTypeOrDummy);
  const { hasPermission: userCanEdit, loading: isLoadingUserCanEdit } =
    useHasPermissionQuery('update:custom_ribbon', undefined);
  const { hasPermission: userCanCreate, loading: isLoadingUserCanCreate } =
    useHasPermissionQuery('insert:custom_ribbon', undefined);

  const canShowEditButton = trpcEnabled
    ? canManage
    : userCanEdit && userCanCreate;
  // Only wait for RBAC loading when the edit button could be shown.
  const isLoading = trpcEnabled
    ? (showEditButton && rbacLoading) || loading
    : (showEditButton && (isLoadingUserCanEdit || isLoadingUserCanCreate)) ||
      loading;
  // Derived boolean rather than the raw array so the publish effect below is
  // keyed to the gate actually flipping, not to `items` getting a fresh
  // reference each render (which would thrash the toolbar store, clear→set).
  const itemsReady = items !== undefined;
  // Relocate mode (CUS-150): publish the edit-modal opener up to the object
  // toolbar instead of rendering the in-ribbon button. Mirror the in-ribbon
  // button's visibility (`showEditButton && canShowEditButton`) and wait for
  // loading so the toolbar only shows the action once the modal can actually
  // open. Clear the slot on unmount / when the button shouldn't show.
  useEffect(() => {
    if (
      !relocateEditToToolbar ||
      !toolbarMounted ||
      !showEditButton ||
      !canShowEditButton ||
      isLoading ||
      // Match the render gate below: don't surface the Edit Filters opener in
      // the toolbar while the ribbon itself is hidden awaiting register data.
      itemsLoading ||
      !itemsReady
    ) {
      clearRibbonConfigState();

      return;
    }
    setRibbonConfigState({
      openEditRibbon: () => setShowEditRibbonModal(true),
    });

    return () => clearRibbonConfigState();
  }, [
    relocateEditToToolbar,
    toolbarMounted,
    showEditButton,
    canShowEditButton,
    isLoading,
    itemsLoading,
    itemsReady,
    setRibbonConfigState,
    clearRibbonConfigState,
  ]);

  const filterResult = responseData?.custom_ribbon[0]?.Filters as
    | FilterModal[]
    | undefined;
  const filters = useMemo<FilterModal[]>(() => {
    if (!filterResult?.length) {
      return defaultFilters;
    }

    return filterResult.map((filter) => {
      return {
        ...filter,
        itemFilterQuery: {
          ...filter.itemFilterQuery,
          tokens: sanitiseTokens(
            filter.itemFilterQuery.tokens as PropertyFilterToken[]
          ),
        },
      };
    });
  }, [defaultFilters, filterResult]);

  // Notify parent component when filters are loaded/changed
  const filtersSignature = useMemo(() => {
    try {
      return JSON.stringify(
        filters.map((f) => ({
          title: f.title,
          itemFilterQuery: f.itemFilterQuery,
        }))
      );
    } catch {
      return String(filters?.length ?? 0);
    }
  }, [filters]);

  useEffect(() => {
    onFiltersChange?.(filters);
    // only re-run when the meaningful content changes (avoid id/reference churn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersSignature, onFiltersChange]);

  useEffect(() => {
    updateScrollState();
  }, [filters, updateScrollState]);

  const onSave: SaveAction<CustomisableRibbonModalFields> = async (
    dataToSave
  ) => {
    if (!parentType) {
      return;
    }
    const { Filters } = dataToSave;

    await upsertCustomRibbon({
      ParentType: parentType,
      Filters: Filters,
      OriginalTimestamp:
        responseData?.custom_ribbon[0]?.ModifiedAtTimestamp ?? null,
      Id: responseData?.custom_ribbon[0]?.Id ?? null,
    });
  };

  if (isLoading || itemsLoading || items === undefined) {
    return null;
  }

  return (
    <Card className={cn('shadow-xs')} data-testid={'customisable-ribbon'}>
      <div className={'relative'}>
        {canScrollLeft && (
          <>
            <button
              aria-label={'Scroll left'}
              className={cn(
                'absolute left-0 top-0 h-full w-6 bg-white hover:bg-[#f8f8f8] border-none p-0 cursor-pointer z-10 flex items-center justify-center'
              )}
              onMouseDown={() => startScrolling('left')}
              onMouseUp={stopScrolling}
              onMouseEnter={() => setHoveredScrollBtn('left')}
              onMouseLeave={() => {
                setHoveredScrollBtn(null);
                stopScrolling();
              }}
            >
              <Icon name={'chevron-left'} variant={'secondary'} />
            </button>
            <div
              aria-hidden={'true'}
              className={`absolute left-6 top-0 h-full w-9 pointer-events-none z-10 bg-gradient-to-r to-white/0 ${hoveredScrollBtn === 'left' ? 'from-[#f8f8f8]' : 'from-white'}`}
            />
          </>
        )}
        {canScrollRight && (
          <>
            <div
              aria-hidden={'true'}
              className={`absolute right-6 top-0 h-full w-9 pointer-events-none z-10 bg-gradient-to-l to-white/0 ${hoveredScrollBtn === 'right' ? 'from-[#f8f8f8]' : 'from-white'}`}
            />
            <button
              aria-label={'Scroll right'}
              className={cn(
                'absolute right-0 top-0 h-full w-6 bg-white hover:bg-[#f8f8f8] border-none p-0 cursor-pointer z-10 flex items-center justify-center'
              )}
              onMouseDown={() => startScrolling('right')}
              onMouseUp={stopScrolling}
              onMouseEnter={() => setHoveredScrollBtn('right')}
              onMouseLeave={() => {
                setHoveredScrollBtn(null);
                stopScrolling();
              }}
            >
              <Icon name={'chevron-right'} variant={'secondary'} />
            </button>
          </>
        )}
        <div
          ref={assignScrollRef}
          className={`flex gap-4 px-6 py-3 flex-grow overflow-x-auto h-full [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${innerClassName ?? ''}`}
          onScroll={updateScrollState}
        >
          {filters.map((filter, index) => (
            <div key={filter.id} className={'flex flex-1 justify-between'}>
              <FilterPropertyDashboardItem
                title={filter.title}
                tableFilterQuery={propertyFilterQuery}
                itemFilterQuery={{
                  tokens: [...filter.itemFilterQuery.tokens],
                  tokenGroups: filter.itemFilterQuery.tokenGroups
                    ? [...filter.itemFilterQuery.tokenGroups]
                    : undefined,
                  operation: filter.itemFilterQuery.operation,
                }}
                items={items ?? []}
                filteringProperties={filteringProperties}
                onClick={onFilterQueryChanged}
                clickThroughEnabled={clickThroughEnabled}
                noClickthroughMessageContent={noClickthroughMessageContent}
              />
              {index !== filters.length - 1 ? (
                <div className={'w-0.5 h-full bg-grey200'} />
              ) : null}
            </div>
          ))}
          {showEditButton &&
            canShowEditButton &&
            !(relocateEditToToolbar && toolbarMounted) && (
              <div className={'flex items-start'}>
                <Button
                  data-testid={'edit-ribbon-button'}
                  iconName={'ellipsis'}
                  variant={'icon'}
                  ariaLabel={t('editRibbon')}
                  onClick={() => setShowEditRibbonModal(true)}
                />
              </div>
            )}
        </div>
      </div>
      {showEditRibbonModal && !loading && (
        <DndContext>
          <ModalForm<CustomisableRibbonModalFields>
            formId={'edit-ribbon-form'}
            header={t('editRibbon')}
            size={'max'}
            onDismiss={() => setShowEditRibbonModal(false)}
            onSave={onSave}
            visible={true}
            i18n={{
              edit_modal_title: t('editRibbon'),
              entity_name: 'Ribbon',
            }}
            schema={CustomisableRibbonFormSchema}
            defaultValues={{
              Filters: defaultFilters,
            }}
            values={{ Filters: filters }}
          >
            <CustomisableRibbonForm
              defaultFilters={defaultFilters}
              filteringProperties={filteringProperties}
              filteringOptions={filteringOptions}
              items={items}
            />
          </ModalForm>
        </DndContext>
      )}
    </Card>
  );
};

export default CustomisableRibbon;
